import type { Case, RecommendedAction, EvidenceKind } from '@/versions/v1/schema/case';
import type { ControlLoopState } from '@/versions/v1/schema/advisory-issue';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { zeroTouchShare } from '@/versions/v1/modules/advisory/RoleScreen.transform';
import { casesRaisedByClock } from '@/versions/v1/modules/advisory/FlowLanding.transform';
import { crewStaffingShortfallPt } from '@/versions/v1/modules/overview/CrewComparison.transform';
import { functionHealthRead, type FunctionHealthRead } from '@/versions/v1/modules/overview/FunctionHealth.transform';
import { formatAffectsLine } from '@/versions/v1/modules/overview/CaseAffects.transform';
import { matchedCauseCategory, isMaintenanceSeatCase } from '@/versions/v1/shared/role-scope';
import { assigneeLabel } from '@/versions/v1/modules/advisory/Scene.transform';
import { mineNudgeRowsForRole } from './MineNudgeRow.transform';
import type { MineNudgeRowModel } from './MineNudgeRow.transform';
import { mineNudges } from '@/versions/v1/shared/mine-nudge.derive';
import { parseDwellHours } from '@/versions/v1/modules/process/Scene.transform';
import {
  CASES,
  ISSUE_THREADS,
  RECOMMENDED_ACTIONS,
} from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  CONTROL_LOOPS,
  EQUIPMENT_HEALTH_INDEX,
  PLANT,
} from '@/versions/v1/shared/bunker-hill-seed';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import {
  daysInMonth,
  weekStart,
  monthStart,
  diffMinutes,
} from '@/versions/v1/shared/clock.derive';
import { trendBundle, seriesAt, readingsAt } from '@/versions/v1/shared/series.derive';
import { juneMtdDays, juneWtdDays } from '@/versions/v1/shared/june-tonnage.derive';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import type { TrendBundle } from '@/versions/v1/shared/series.derive';
import type { Reading } from '@/versions/v1/schema/reading';
import {
  readingComparisonBasis,
  deviationFromBasisPct,
  BASIS_LABEL,
  type ComparisonBasisRead,
} from '@/versions/v1/shared/comparison-basis';
import { metRecoveryKpiRead } from '@/versions/v1/shared/recovery-vocab.derive';
import {
  formatMoney,
  formatMoneyK,
  formatMoneyPerT,
  formatSignedPct,
  nsrRead,
  NSR_SYN_PROVENANCE,
  operatingCostRead,
  recoveryPayableLevers,
} from '@/versions/v1/shared/NsrEngine';

export type PerformanceRoleId = 'gm' | 'superintendent' | 'metallurgist' | 'cro' | 'maintenance';

export type PerformanceInput = {
  cases: readonly Case[];
  loops: readonly ControlLoopState[];
  actions: readonly RecommendedAction[];
};

export type PerformanceOptions = {
  showMoney?: boolean;
};

export type DeviationStatus = 'ok' | 'watch' | 'below' | 'gap';

export type PerformanceKpi = {
  label: string;
  value: string;
  caption?: string;
  tone: VizTone;
  provenance?: string;
  /** Plain-word chip on the glass; full citation lives in `provenance` (tooltip). */
  provenanceChip?: string;
};

export type ByAreaRow = {
  id: string;
  area: string;
  variable: string;
  dailyCell: string;
  weeklyCell: string;
  mtdCell: string;
  /** Shown in row detail when design differs from the column basis. */
  designDetail?: string;
  status: DeviationStatus;
};

export type PlanAchievementRow = {
  period: string;
  periodSubLabel: string;
  actualTd: number;
  planTd: number;
  pctOfPlan: number;
  provenance?: string;
  /** Plain-word chip on the glass; full citation lives in `provenance` (tooltip). */
  provenanceChip?: string;
};

export type AiBandPanel = {
  id: string;
  title: string;
  subtitle?: string;
  footnote?: string;
  provenance?: string;
  /** Plain-word chip on the glass; full citation lives in `provenance` (tooltip). */
  provenanceChip?: string;
  body?: string;
  rows?: readonly {
    label: string;
    content: string;
    provenance: string;
    confirmed?: boolean;
    pillText?: string;
  }[];
  forecastMeters?: readonly {
    label: string;
    value: number;
    tone: VizTone;
    caption: string;
    shiftAttribution?: string;
  }[];
  forecastLine?: string;
  forecastGapLine?: string;
  mineNudgeRows?: readonly MineNudgeRowModel[];
  causeLines?: readonly { label: string; td: number }[];
  trendCategories?: readonly string[];
  trendPoints?: readonly number[];
  trendYDomain?: readonly [number, number];
  labelLastPoint?: boolean;
  threadCards?: readonly {
    title: string;
    confirmedPill: string;
    clauses: readonly { text: string; confirmed: boolean; provenance: string; pillText?: string }[];
  }[];
  contextCards?: readonly {
    title: string;
    borderTone: VizTone;
    body: string;
    clauses: readonly { text: string; confirmed: boolean; provenance: string; pillText?: string }[];
  }[];
  checklist?: readonly { text: string; tail: string }[];
  valueRows?: readonly { sign: '+' | '-'; text: string }[];
};

export type PerformanceViewModel = {
  /** Landing subtitle, under the page title (the GM landing reads it). */
  title: string;
  kpis: PerformanceKpi[];
  byAreaRows?: ByAreaRow[];
  /** `ComparisonBasisRead.label` shared by every row in `byAreaRows` — all
   * of them resolve through the same ranking, so one label represents the
   * whole table (pl.D1 round 2, #142); read here, never hardcoded on the
   * column headers. */
  byAreaBasisLabel?: string;
  planAchievement?: {
    tier: 2 | 3;
    rows: PlanAchievementRow[];
    subtitle: string;
    footnote: string;
    columnProvenance?: string;
  };
  functionHealth?: FunctionHealthRead;
  grindingShortfall?: readonly { caseId: string; title: string; tdEst: number }[];
  aiBand?: {
    subtitle: string;
    layout: 'gm-full' | 'met-split' | 'cro-split';
    briefingTitle?: string;
    panels: readonly AiBandPanel[];
  };
};

const CONFIRMED_STATES: readonly Case['state'][] = [
  'diagnosed',
  'outcome-documented',
  'fmea-review',
  'approved',
];

const OPEN_CASE_STATES: readonly Case['state'][] = [
  'detected',
  'matched',
  'routed',
  'evidence-requested',
  'escalated',
  'diagnosed',
  'outcome-documented',
  'fmea-review',
];

function kpi(id: string): Reading {
  const row = readingByIdLookup(id);
  if (!row) throw new Error(`Missing reading ${id}`);
  return row;
}

function readingNumber(reading: Reading, field: 'value' | 'design' | 'target'): number {
  const n = reading[field];
  return n ?? 0;
}

function readingAt(id: string, state: SessionState): Reading {
  return readingsAt(state).find((r) => r.id === id) ?? kpi(id);
}

function comparisonBasis(id: string, state: SessionState): ComparisonBasisRead | null {
  return readingComparisonBasis(readingAt(id, state));
}

function formatDeltaPct(pct: number): string {
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

function basisCaption(
  actual: number,
  read: ComparisonBasisRead,
  decimals = 0,
  unit = '',
): { primary: string; secondary?: string; basisValue: number } {
  const pct = deviationFromBasisPct(actual, read);
  const basisFmt = `${formatNumber(read.value, decimals)}${unit}`;
  const primary = `vs ${read.label} ${basisFmt} · ${formatDeltaPct(pct)}`;
  let secondary: string | undefined;
  if (
    read.design !== undefined &&
    read.outranked.includes('design') &&
    read.design !== read.value
  ) {
    const designPct = ((actual - read.design) / read.design) * 100;
    secondary = `design ${formatNumber(read.design, decimals)}${unit} · ${formatDeltaPct(designPct)}`;
  }
  return { primary, secondary, basisValue: read.value };
}

/** Tone for a tile whose caption may carry a `basisCaption` secondary — a
 * shown design figure is the open case (ruling #258 round 2, "captions vs
 * tone"); tone must judge against it, not silently against the winning
 * (ramp) basis the caption also states. Falls back to the winning basis
 * when no distinct design secondary is shown. */
function basisCaptionTone(actual: number, read: ComparisonBasisRead): VizTone {
  const showsDesignSecondary =
    read.design !== undefined && read.outranked.includes('design') && read.design !== read.value;
  const target = showsDesignSecondary ? read.design! : read.value;
  return deviationToTone(deviationStatusPct(actual, target));
}

function oreTdBasis(state: SessionState): ComparisonBasisRead {
  const read = comparisonBasis('rd-kpi-ore-td', state);
  if (!read) throw new Error('Missing ore-td comparison basis');
  return read;
}

function kpiBasisValue(id: string, state: SessionState): number {
  const read = comparisonBasis(id, state);
  if (!read) return readingNumber(readingAt(id, state), 'value');
  return read.value;
}

export function recoveryTrend(state: SessionState): TrendBundle {
  return trendBundle(['rs-zn-rec', 'rs-pb-rec', 'rs-ag-rec'], state, { points: 7 });
}

function kpiFromState(id: string, state: SessionState, field: 'value' | 'design' | 'target'): number {
  const projected = readingsAt(state).find((r) => r.id === id);
  if (!projected) return readingNumber(kpi(id), field);
  const n = projected[field];
  return n ?? readingNumber(kpi(id), field);
}

function kpiValueState(id: string, state: SessionState): number {
  return kpiFromState(id, state, 'value');
}

export function decimalPlacesFromSeed(value: string): number {
  const match = value.match(/\.(\d+)/);
  return match ? match[1].length : 0;
}

function kpiValue(id: string): number {
  return readingNumber(kpi(id), 'value');
}

function kpiDesign(id: string): number {
  return readingNumber(kpi(id), 'design');
}

function kpiCell(id: string, field: 'value' | 'design' | 'target'): string {
  const row = kpi(id);
  const n = row[field];
  if (n === undefined) return '—';
  const dec = row.decimals ?? (row.unit === '%' ? 1 : 0);
  return formatNumber(n, dec);
}

export function deviationStatusPct(actual: number, target: number): DeviationStatus {
  if (target <= 0) return 'gap';
  const pct = ((actual - target) / target) * 100;
  if (pct >= -0.5) return 'ok';
  if (pct >= -1.5) return 'watch';
  return 'below';
}

export function deviationToTone(status: DeviationStatus): VizTone {
  if (status === 'ok') return 'ok';
  if (status === 'watch') return 'warn';
  if (status === 'gap') return 'neutral';
  return 'bad';
}

/** One tone rule per comparator (K10c item 5): a reading judged against the
 * comparator its caption names — above it is good, below it is `watch` inside
 * `deviationStatusPct`'s bands and `bad` beyond. The CRO landing's KPI tiles
 * and the Operator landing's recovery tiles both read this, so the same
 * Zn-recovery-vs-Budget comparison cannot tone green on one landing and red
 * on the other. */
export function comparatorTone(actual: number, basis: number): VizTone {
  return deviationToTone(deviationStatusPct(actual, basis));
}

export function planAchievementStatus(pct: number): VizTone {
  if (pct >= 99.5) return 'ok';
  if (pct >= 97) return 'warn';
  return 'bad';
}

export function isCauseConfirmed(caseItem: Case): boolean {
  return CONFIRMED_STATES.includes(caseItem.state);
}

const EVIDENCE_KIND_LABEL: Record<EvidenceKind, string> = {
  'lab-sample': 'lab sample',
  visual: 'visual check',
  'loop-state': 'loop state',
};

export type ClauseEvidenceRead = {
  confirmed: boolean;
  pillText: string;
  evidenceKind?: EvidenceKind;
  askedRole?: Case['assignedTo'];
};

export function clauseEvidence(caseItem: Case): ClauseEvidenceRead {
  const openRequest = caseItem.evidenceRequests.find((row) => row.status === 'open');
  const answered = caseItem.evidenceRequests.find((row) => row.status === 'answered');

  if (isCauseConfirmed(caseItem)) {
    const kind = answered?.kind ?? openRequest?.kind;
    if (!kind) {
      return { confirmed: true, pillText: 'Confirmed' };
    }
    return {
      confirmed: true,
      pillText: `Confirmed · ${EVIDENCE_KIND_LABEL[kind]}`,
      evidenceKind: kind,
    };
  }

  if (openRequest) {
    return {
      confirmed: false,
      pillText: `Open · ${EVIDENCE_KIND_LABEL[openRequest.kind]} · asked ${assigneeLabel(openRequest.target)}`,
      evidenceKind: openRequest.kind,
      askedRole: openRequest.target,
    };
  }

  return {
    confirmed: false,
    pillText: 'Unconfirmed · no request',
  };
}

export function isOpenCase(caseItem: Case): boolean {
  return OPEN_CASE_STATES.includes(caseItem.state);
}

export function loopsManualPastHours(
  loops: readonly ControlLoopState[],
  hours: number,
): ControlLoopState[] {
  return loops.filter((loop) => {
    if (loop.mode !== 'manual') return false;
    const dwell = parseDwellHours(loop.duration);
    return dwell !== null && dwell > hours;
  });
}

function formatNumber(n: number, decimals = 0): string {
  return n.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function formatActualTarget(
  actual: number,
  target: number,
  unit = '',
  actualSeed?: string,
): { actual: string; target: string } {
  const dec = unit === '%' ? 1 : actualSeed ? decimalPlacesFromSeed(actualSeed) : 0;
  return {
    actual: `${formatNumber(actual, dec)}${unit}`,
    target: `${formatNumber(target, dec)}${unit}`,
  };
}

function distributeShortfall(cases: readonly Case[], magnitude: number): number[] {
  if (cases.length === 0 || magnitude <= 0) return [];
  const weights = cases.map((c) => (c.severity === 'high' ? 2 : c.severity === 'med' ? 1 : 0.5));
  const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
  const raw = weights.map((weight) => (magnitude * weight) / weightSum);
  const rounded = raw.map((value) => -Math.round(value));
  const diff = -magnitude - rounded.reduce((sum, value) => sum + value, 0);
  rounded[0] += diff;
  return rounded;
}

const READING_SERIES_ID: Record<string, string> = {
  'rd-kpi-ore-td': 'rs-ore-td',
  'rd-kpi-pb-rec': 'rs-pb-rec',
  'rd-kpi-pb-grade': 'rs-pb-grade',
  'rd-kpi-ag-rec': 'rs-ag-rec',
  'rd-kpi-ag-grade': 'rs-ag-grade',
  'rd-kpi-zn-rec': 'rs-zn-rec',
  'rd-kpi-zn-grade': 'rs-zn-grade',
  'rd-unit-flzn-moist': 'rs-flzn-moist',
};

function buildByAreaRows(state: SessionState): ByAreaRow[] {
  return [
    rowFromTriadReading('area-grind', 'Grinding', 'Throughput (t/d)', 'rd-kpi-ore-td', '', state),
    rowFromTriadReading('area-pb-rec', 'Flotation — Pb', 'Recovery (%)', 'rd-kpi-pb-rec', '%', state),
    rowFromTriadReading(
      'area-pb-grade',
      'Flotation — Pb',
      'Concentrate grade (% Pb)',
      'rd-kpi-pb-grade',
      '%',
      state,
    ),
    rowFromTriadReading('area-ag-rec', 'Flotation — Pb', 'Ag recovery (%)', 'rd-kpi-ag-rec', '%', state),
    rowFromTriadReading(
      'area-ag-grade',
      'Flotation — Pb',
      'Ag grade (oz/t)',
      'rd-kpi-ag-grade',
      ' oz/t',
      state,
    ),
    rowFromTriadReading('area-zn-rec', 'Flotation — Zn', 'Recovery (%)', 'rd-kpi-zn-rec', '%', state),
    rowFromTriadReading(
      'area-zn-grade',
      'Flotation — Zn',
      'Concentrate grade (% Zn)',
      'rd-kpi-zn-grade',
      '%',
      state,
    ),
    rowFromTriadReading(
      'area-filter',
      'Filtering / Thickening',
      'Filter cake moisture',
      'rd-unit-flzn-moist',
      '%',
      state,
    ),
  ];
}

/** Mean of a series' measured points in the closed window `[from, to)` — the
 *  bounded twin of `windowMean`, which runs to the cursor and so would pull in
 *  the open production day. K21 (#719): the Overview's WTD column covers
 *  exactly the plant week's complete production days (`juneWtdDays`), the same
 *  six days Metal accounting's `wtdWindow()` and `/meeting` sum, and its MTD
 *  column the cursor month's complete production days (`juneMtdDays`), so
 *  every WTD and MTD figure on the page is one window. */
function meanWindowIn(seriesId: string, state: SessionState, from: string, to: string): number {
  const points = seriesAt(seriesId, state).points.filter(
    (point): point is { at: string; value: number } =>
      point.value !== null && diffMinutes(point.at, from) >= 0 && diffMinutes(point.at, to) < 0,
  );
  if (points.length === 0) return 0;
  return points.reduce((sum, point) => sum + point.value, 0) / points.length;
}

function rowFromTriadReading(
  id: string,
  area: string,
  variable: string,
  readingId: string,
  unit = '',
  state: SessionState,
): ByAreaRow {
  const projected = readingAt(readingId, state);
  const basis = comparisonBasis(readingId, state);
  if (!basis) {
    return {
      id,
      area,
      variable,
      dailyCell: '—',
      weeklyCell: '—',
      mtdCell: '—',
      status: 'gap',
    };
  }
  const actual = readingNumber(projected, 'value');
  const dec = projected.decimals ?? (unit === '%' ? 1 : 0);
  const { secondary, basisValue } = basisCaption(actual, basis, dec, unit);
  const daily = formatActualTarget(actual, basisValue, unit, formatNumber(actual, dec));
  const seriesId = READING_SERIES_ID[readingId];
  const wtdDays = juneWtdDays(state);
  const wtdFrom = weekStart(state.cursor);
  const wtdTo = wtdDays[wtdDays.length - 1]?.closedAt;
  const mtdDays = juneMtdDays(state);
  const mtdFrom = monthStart(state.cursor);
  const mtdTo = mtdDays[mtdDays.length - 1]?.closedAt;
  // K21 (#719) / D25: the WTD cell is the mean over the plant week's complete
  // production days, bounded at the open day's own stamp. K21 round 1 puts the
  // MTD cell on the same rule — the mean over the cursor month's complete
  // production days (`juneMtdDays`), bounded at the open day's own stamp too,
  // so the open 08 Jun point (published whole at 07 Jun 18:00) is in neither
  // mean. `meanWindowIn` is the bounded twin of `windowMean`.
  const weeklyActual = seriesId && wtdTo ? meanWindowIn(seriesId, state, wtdFrom, wtdTo) : actual;
  const mtdActual = seriesId && mtdTo ? meanWindowIn(seriesId, state, mtdFrom, mtdTo) : actual;
  const weeklyFmt = formatNumber(weeklyActual, dec);
  const mtdFmt = formatNumber(mtdActual, dec);
  const weekly = formatActualTarget(weeklyActual, basisValue, unit, weeklyFmt);
  const mtd = formatActualTarget(mtdActual, basisValue, unit, mtdFmt);
  const dailyPct = deviationFromBasisPct(actual, basis);
  const weeklyPct = deviationFromBasisPct(weeklyActual, basis);
  const mtdPct = deviationFromBasisPct(mtdActual, basis);
  const seedReading = readingByIdLookup(readingId);
  const status =
    seedReading?.higherIsBetter === false
      ? deviationStatusPct(basisValue, actual)
      : deviationStatusPct(actual, basisValue);
  return {
    id,
    area,
    variable,
    dailyCell: `${daily.actual} · ${formatDeltaPct(dailyPct)}`,
    weeklyCell: `${weekly.actual} · ${formatDeltaPct(weeklyPct)}`,
    mtdCell: `${mtd.actual} · ${formatDeltaPct(mtdPct)}`,
    designDetail: secondary,
    status,
  };
}

function buildPlanAchievement(
  tier: 2 | 3,
  state: SessionState,
): PerformanceViewModel['planAchievement'] {
  const planBasis = oreTdBasis(state);
  const dailyPlan = planBasis.value;
  const actual24h = kpiValueState('rd-kpi-ore-td', state);
  const pct24h = (actual24h / dailyPlan) * 100;
  // D25 / K21 (#719): the week's actual and elapsed days are the plant week's
  // **complete production days** (`juneWtdDays`) — the same six days at the
  // cursor that Metal accounting's `wtdWindow()` and `/cost` publish — so the
  // Overview's WTD row is 10,652 dst against plan 6 × 1,440 = 8,640, not the
  // open-ended week-start sum of 12,372 over 7 days that counted the open
  // 08 Jun day whole.
  const wtdDays = juneWtdDays(state);
  const weekDays = wtdDays.length;
  const planWeek = dailyPlan * weekDays;
  const actualWeek = wtdDays.reduce((sum, day) => sum + day.dst, 0);
  const pctWeek = planWeek > 0 ? (actualWeek / planWeek) * 100 : 0;
  // D25 / K17 round 3 (#663): the month's actual and elapsed days are the
  // cursor month's **complete production days** — the same `juneMtdDays` window
  // Metal accounting, Planning and `/reports` read — not every point from the
  // month start, which counted the open 8 Jun day whole (14,121 dst over 8
  // days, where the one June figure is 12,401 dst over 7).
  const mtdDays = juneMtdDays(state);
  const elapsedMonthDays = mtdDays.length;
  const monthDays = daysInMonth(state.cursor);
  const planMtd = dailyPlan * elapsedMonthDays;
  const actualMtd = mtdDays.reduce((sum, day) => sum + day.dst, 0);
  const pctMtd = planMtd > 0 ? (actualMtd / planMtd) * 100 : 0;

  return {
    tier,
    subtitle: tier === 3 ? '' : 'Superintendent scope',
    // Names the basis that actually won ("Plan"), not a hardcoded "ramp"
    // (pl.D1 round 2, #142) — dailyPlan is planBasis.value, so the words
    // must track whichever basis planBasis resolved to.
    footnote: `Plan = ${planBasis.label.toLowerCase()} × elapsed days. Week/MTD actuals from seeded ore-t/d series.`,
    columnProvenance: '[syn]',
    rows: [
      {
        period: 'Last 24 h',
        periodSubLabel: 'Today',
        actualTd: actual24h,
        planTd: dailyPlan,
        pctOfPlan: pct24h,
      },
      {
        period: 'Week to date',
        periodSubLabel: `Day ${weekDays} of 7`,
        actualTd: Math.round(actualWeek),
        planTd: planWeek,
        pctOfPlan: pctWeek,
        provenance: '[syn]',
      },
      {
        period: 'Month to date',
        periodSubLabel: `Day ${elapsedMonthDays} of ${monthDays}`,
        actualTd: Math.round(actualMtd),
        planTd: planMtd,
        pctOfPlan: pctMtd,
        provenance: '[syn]',
      },
    ],
  };
}

function buildIssueThreadCards(cases: readonly Case[]): AiBandPanel['threadCards'] {
  const caseById = new Map(cases.map((row) => [row.id, row]));
  return ISSUE_THREADS.map((thread) => {
    const group = thread.caseIds
      .map((id) => caseById.get(id))
      .filter((row): row is Case => Boolean(row && isOpenCase(row)));
    const confirmed = group.filter(isCauseConfirmed).length;
    const clauses = group
      .flatMap((caseItem) => {
        const evidence = clauseEvidence(caseItem);
        return {
          text: caseItem.causeCandidate,
          confirmed: evidence.confirmed,
          provenance: caseItem.code ?? caseItem.id,
          pillText: evidence.pillText,
        };
      })
      .sort((a, b) => Number(b.confirmed) - Number(a.confirmed))
      .slice(0, 3);
    return {
      title: thread.title,
      confirmedPill: `${confirmed} of ${group.length} confirmed`,
      clauses,
    };
  });
}

/** Mean of a shift series' measured points in `[from, to)`; an absent `to`
 *  keeps the cursor-inclusive top. K21 round 1 (#719) / D25 passes the open
 *  production day's own stamp as `to`, so the MTD shift attribution counts the
 *  cursor month's complete production days and not the open 08 Jun day's
 *  night and day shift points. */
function shiftSeriesMean(
  seriesId: string,
  state: SessionState,
  from: string,
  kind: 'day' | 'night',
  to?: string,
): number {
  const view = seriesAt(seriesId, state);
  const pts = view.points.filter(
    (p): p is { at: string; value: number } =>
      p.value !== null &&
      diffMinutes(p.at, from) >= 0 &&
      (to === undefined ? diffMinutes(p.at, state.cursor) <= 0 : diffMinutes(p.at, to) < 0) &&
      (kind === 'day' ? p.at.endsWith('T06:00') : p.at.endsWith('T18:00')),
  );
  if (pts.length === 0) return 0;
  return pts.reduce((s, p) => s + p.value, 0) / pts.length;
}

function metalShiftAttributionLine(
  readingId: string,
  seriesId: string,
  state: SessionState,
): string {
  const basis = comparisonBasis(readingId, state);
  if (!basis) return '';
  // K21 round 1 (#719) / D25: MTD is the cursor month's complete production
  // days, so the shift means stop at the open day's own stamp (`juneMtdDays`'
  // last closed day) instead of running to the cursor.
  const mtdDays = juneMtdDays(state);
  const mtdFrom = monthStart(state.cursor);
  const mtdTo = mtdDays[mtdDays.length - 1]?.closedAt;
  const dayMean = shiftSeriesMean(seriesId, state, mtdFrom, 'day', mtdTo);
  const nightMean = shiftSeriesMean(seriesId, state, mtdFrom, 'night', mtdTo);
  const dayGap = dayMean - basis.value;
  const nightGap = nightMean - basis.value;
  const metal = readingId.includes('pb') ? 'Pb' : readingId.includes('zn') ? 'Zn' : 'Ag';
  const fmt = (gap: number) => `${gap >= 0 ? '+' : ''}${gap.toFixed(1)}`;
  return `${metal} recovery · Day ${fmt(dayGap)} · Night ${fmt(nightGap)} pts vs ${basis.label.toLowerCase()} MTD`;
}

function buildRecoveryAttributionMeters(
  state: SessionState,
): AiBandPanel['forecastMeters'] {
  const specs = [
    { readingId: 'rd-kpi-pb-rec', seriesId: 'rs-pb-rec-shift', label: 'Pb recovery' },
    { readingId: 'rd-kpi-zn-rec', seriesId: 'rs-zn-rec-shift', label: 'Zn recovery' },
    { readingId: 'rd-kpi-ag-rec', seriesId: 'rs-ag-rec-shift', label: 'Ag recovery' },
  ] as const;
  return specs.map(({ readingId, seriesId, label }) => {
    const basis = comparisonBasis(readingId, state);
    const actual = kpiValueState(readingId, state);
    const pct = basis ? (actual / basis.value) * 100 : 100;
    return {
      label,
      value: pct,
      tone: basis ? deviationToTone(deviationStatusPct(actual, basis.value)) : 'info',
      caption: metalShiftAttributionLine(readingId, seriesId, state),
    };
  });
}

function buildForecastMeters(
  state: SessionState,
  opts?: { shiftAttribution?: boolean },
): AiBandPanel['forecastMeters'] {
  if (opts?.shiftAttribution) {
    return buildRecoveryAttributionMeters(state);
  }
  const rate = kpiValueState('rd-kpi-ore-td', state);
  // K21 round 2 (#719): the pro-rated Week-to-date and Month-to-date rows are
  // deleted. They were unreachable on the glass, and each multiplied the cursor
  // rate by elapsed calendar days — a second WTD figure that would contradict
  // the shared complete-day window `juneWtdDays` the rest of the page reads.
  // Only the trailing-24 h row remains, which is the reading itself.
  const periods = [{ label: 'Last 24 h', days: 1 }];
  return periods.map((period) => {
    const projected = rate * period.days;
    const planPeriod = oreTdBasis(state).value * period.days;
    const actualToDate = rate * period.days;
    const pct = (projected / planPeriod) * 100;
    const baseCaption = `${formatNumber(Math.round(actualToDate))} t / ${formatNumber(Math.round(planPeriod))} t plan`;
    return {
      label: period.label,
      value: pct,
      tone: planAchievementStatus(pct),
      caption: baseCaption,
    };
  });
}

/**
 * Comp adjudication 6 (polish.P28b, row 324): zero-touch has one function. The
 * Overview reads `zeroTouchShare` on the cases registered at the clock, the
 * same set and figure the Advisory role screen reads.
 */
export function overviewZeroTouchShare(cases: readonly Case[], state: SessionState): ReturnType<typeof zeroTouchShare> {
  return zeroTouchShare(casesRaisedByClock(cases, state.cursor));
}

function buildLearningTrend(cases: readonly Case[], state: SessionState): Pick<AiBandPanel, 'trendCategories' | 'trendPoints' | 'trendYDomain' | 'labelLastPoint'> {
  const zeroTouch = overviewZeroTouchShare(cases, state);
  const months = ['Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  const end = zeroTouch.pct;
  const start = Math.max(10, end - 25);
  const step = months.length <= 1 ? 0 : (end - start) / (months.length - 1);
  const trendPoints = months.map((_, index) => Math.round(start + step * index));
  trendPoints[trendPoints.length - 1] = end;
  return {
    trendCategories: months,
    trendPoints,
    trendYDomain: [0, 100] as const,
    labelLastPoint: true,
  };
}

export type CauseRoutingRead = {
  metallurgical: number;
  maintenance: number;
  staffing: number;
  totalGap: number;
};

function caseBucket(caseItem: Case): 'metallurgical' | 'maintenance' | 'staffing' | 'unrouted' {
  const category = matchedCauseCategory(caseItem);
  if (caseItem.assignedTo === 'maintenance' || category === 'mechanical-electrical') {
    return 'maintenance';
  }
  if (caseItem.assignedTo === 'metallurgist' || category === 'metallurgical-process') {
    return 'metallurgical';
  }
  if (caseItem.assignedTo === 'ops-supervisor' || caseItem.assignedTo === 'operator') {
    return 'staffing';
  }
  return 'unrouted';
}

function shortfallSeverityWeight(caseItem: Case): number {
  if (caseItem.severity === 'high') return 2;
  if (caseItem.severity === 'med') return 1;
  return 0.5;
}

function bucketMagnitudeTd(cases: readonly Case[], share: number): number {
  if (share <= 0 || cases.length === 0) return 0;
  return distributeShortfall(cases, share).reduce((sum, value) => sum + Math.abs(value), 0);
}

function crewEffectShortfallTd(state: SessionState, cases: readonly Case[], actualTd: number): number {
  const effectPt = crewStaffingShortfallPt(state, cases);
  return Math.round((effectPt / 100) * actualTd);
}

export function causeRoutingRead(state: SessionState, cases: readonly Case[]): CauseRoutingRead {
  const actualTd = kpiValueState('rd-kpi-ore-td', state);
  const designTd = readingNumber(readingAt('rd-kpi-ore-td', state), 'design');
  const totalGap = Math.max(0, Math.round(designTd - actualTd));
  if (totalGap === 0) {
    return { metallurgical: 0, maintenance: 0, staffing: 0, totalGap: 0 };
  }

  const shortfallCases = cases.filter(
    (c) =>
      isOpenCase(c) &&
      (c.severity === 'high' || c.severity === 'med') &&
      caseIsBelowComparisonBasis(c, state),
  );
  const metallurgicalCases = shortfallCases.filter((c) => caseBucket(c) === 'metallurgical');
  const maintenanceCases = shortfallCases.filter((c) => caseBucket(c) === 'maintenance');

  const staffing = crewEffectShortfallTd(state, cases, actualTd);
  const remaining = Math.max(0, totalGap - staffing);
  const metWeight = metallurgicalCases.reduce((sum, c) => sum + shortfallSeverityWeight(c), 0);
  const maintWeight = maintenanceCases.reduce((sum, c) => sum + shortfallSeverityWeight(c), 0);
  const routedWeight = metWeight + maintWeight;

  let metallurgical = 0;
  let maintenance = 0;
  if (routedWeight > 0) {
    const metShare = Math.round((remaining * metWeight) / routedWeight);
    const maintShare = remaining - metShare;
    metallurgical = bucketMagnitudeTd(metallurgicalCases, metShare);
    maintenance = bucketMagnitudeTd(maintenanceCases, maintShare);
  }

  const diff = totalGap - metallurgical - maintenance - staffing;
  if (diff !== 0) {
    if (metallurgical >= maintenance) metallurgical += diff;
    else maintenance += diff;
  }

  return { metallurgical, maintenance, staffing, totalGap };
}

function maintenanceBriefingClause(cases: readonly Case[], state: SessionState): string {
  const ranked = [...EQUIPMENT_HEALTH_INDEX].sort((a, b) => a.healthPct - b.healthPct);
  const lowest = ranked[0];
  const maintCases = cases.filter((c) => isOpenCase(c) && isMaintenanceSeatCase(c));
  const maintCount = maintCases.length;
  const causeRouting = causeRoutingRead(state, cases);
  const assetPart = `${lowest.asset} ${lowest.healthPct}%`;
  const bmCase = cases.find((c) => c.id === 'case-bm-brg');
  const forwardRisk = bmCase?.affects ? formatAffectsLine(bmCase, state) : undefined;
  const parts = [
    `Maintenance ${assetPart}`,
    `${maintCount} open case${maintCount === 1 ? '' : 's'}`,
    `${formatNumber(causeRouting.maintenance)} t/d realised gap`,
  ];
  if (forwardRisk) parts.push(forwardRisk);
  return `${parts.join(' · ')}.`;
}

function buildFullAiBand(
  cases: readonly Case[],
  state: SessionState,
  briefingTitle = 'AI briefing — this month',
  opts?: { shiftAttribution?: boolean },
): PerformanceViewModel['aiBand'] {
  const actualTd = kpiValueState('rd-kpi-ore-td', state);
  const byArea = buildByAreaRows(state);
  const flaggedAreas = byArea.filter((row) => row.status !== 'ok');
  const topCase = cases.find((c) => c.id === 'case-zn-recovery') ?? cases.find(isOpenCase);
  const zeroTouch = overviewZeroTouchShare(cases, state);
  const throughputBasis = oreTdBasis(state);
  const diagnosis = diagnose(state);
  const topCandidate = diagnosis.candidates[0];
  const gmNsr = nsrRead(state);
  const causeRouting = opts?.shiftAttribution ? undefined : causeRoutingRead(state, cases);
  const designTd = readingNumber(readingAt('rd-kpi-ore-td', state), 'design');
  const briefingParts = [
    `Throughput ${formatNumber(actualTd)} t/d ${basisCaption(actualTd, throughputBasis).primary}.`,
    flaggedAreas.length > 0
      ? `${flaggedAreas.length} process area(s) below watch threshold.`
      : 'Process areas largely on target.',
    topCandidate
      ? `${topCandidate.rule.diagnosis} — ${topCandidate.confidencePct}% confidence from series · rules.`
      : topCase
        ? `${topCase.code ?? topCase.id}: ${topCase.observation}`
        : 'No dominant open case in seed.',
    `Zero-touch share ${zeroTouch.pct}% · ${zeroTouch.count} of ${zeroTouch.total} seeded cases.`,
  ];
  if (!opts?.shiftAttribution) {
    briefingParts.push(maintenanceBriefingClause(cases, state));
    if (causeRouting && causeRouting.totalGap > 0) {
      const nsrPerTd = gmNsr.daily.total / actualTd;
      const gapConsequence = Math.round(nsrPerTd * causeRouting.totalGap);
      briefingParts.push(
        `Design gap ${formatNumber(causeRouting.totalGap)} t/d ≈ ${formatMoney(gapConsequence)}/d of NSR at today's realisation.`,
      );
    }
  }
  const learning = buildLearningTrend(cases, state);

  return {
    subtitle: '',
    layout: 'gm-full',
    briefingTitle,
    panels: [
      {
        id: 'ai-briefing',
        title: briefingTitle,
        body: briefingParts.join(' '),
        provenance: 'Computed from startup table · open cases · zero-touch mix',
        provenanceChip: 'derived',
      },
      {
        id: 'ai-forecast',
        title: 'Forecast to close',
        subtitle: opts?.shiftAttribution
          ? 'Recovery shift attribution · MTD vs design'
          : 'Hold-current-rate projection',
        provenance: 'Computed from series · rules',
        provenanceChip: 'derived',
        forecastMeters: opts?.shiftAttribution
          ? buildForecastMeters(state, opts)
          : (buildForecastMeters(state, opts) ?? []).slice(0, 1),
        forecastGapLine:
          causeRouting && causeRouting.totalGap > 0
            ? `${formatNumber(causeRouting.totalGap)} t/d vs design ${formatNumber(designTd)}`
            : undefined,
        mineNudgeRows: opts?.shiftAttribution
          ? undefined
          : mineNudgeRowsForRole(mineNudges(state), 'gm'),
        causeLines: causeRouting
          ? [
              { label: 'Metallurgical', td: causeRouting.metallurgical },
              { label: 'Maintenance', td: causeRouting.maintenance },
              { label: 'Staffing', td: causeRouting.staffing },
            ]
          : undefined,
      },
      {
        id: 'ai-correlation',
        title: 'Cross-signal correlation',
        subtitle: 'Open case threads',
        threadCards: (buildIssueThreadCards(cases) ?? []).slice(0, 1),
      },
      {
        id: 'ai-learning',
        title: 'Learning-loop trend',
        subtitle: 'Zero-touch case rate',
        provenance: '[syn]',
        ...learning,
      },
    ],
  };
}

function croRecoveryLeverRows(state: SessionState): AiBandPanel['valueRows'] {
  return recoveryPayableLevers(state).map(({ metal, gapPt, leverPerPt, unit }) => {
    const sign = gapPt >= 0 ? '+' : '−';
    return {
      sign: gapPt >= 0 ? '+' : '-',
      text: `${metal} ${sign}${Math.abs(gapPt).toFixed(1)} pt vs design · ${leverPerPt} ${unit} per +1 pt`,
    };
  });
}

function buildCroAiBand(
  cases: readonly Case[],
  actions: readonly RecommendedAction[],
  state: SessionState,
  showMoney = true,
): PerformanceViewModel['aiBand'] {
  const croNsr = nsrRead(state);
  const znCase = cases.find((c) => c.id === 'case-zn-recovery');
  const evidence =
    znCase?.evidenceRequests.filter(
      (row) => row.status === 'open' && (row.target === 'operator' || row.target === 'cro'),
    ) ?? [];

  return {
    subtitle: '',
    layout: 'cro-split',
    panels: [
      {
        id: 'cro-zn-cause',
        title: 'Why Zn recovery is below design',
        provenance: znCase?.code ?? 'case-zn-recovery',
        rows: znCase
          ? [
              {
                label: 'Observation',
                content: znCase.observation,
                provenance: znCase.code ?? 'ZN-REC',
                confirmed: true,
                pillText: clauseEvidence(znCase).pillText,
              },
              {
                label: 'Cause candidate',
                content: znCase.causeCandidate,
                provenance: znCase.code ?? 'ZN-REC',
                confirmed: isCauseConfirmed(znCase),
                pillText: clauseEvidence(znCase).pillText,
              },
            ]
          : [],
        checklist: [
          ...evidence.map((row) => ({ text: row.text, tail: row.kind })),
          ...actions
            .filter((row) => row.status === 'proposed' && row.caseId === 'case-zn-recovery')
            .map((row) => ({ text: row.text, tail: row.urgency })),
        ],
      },
      {
        id: 'cro-net-value',
        title: showMoney
          ? 'Recovery optimisation — net value, not just recovery'
          : 'Recovery optimisation — payable metal levers',
        // narration-ok: Module 3 §8.9 · Module 12 §2 is tooltip-only via provenance on the basis chip
        provenance: showMoney
          ? 'Module 3 §8.9 · Module 12 §2 — illustrative smelter schedule (D-money)'
          : 'Recovery points and payable metal — head grade · smelter schedule',
        provenanceChip: '[syn]',
        body: showMoney ? `Net value ${formatMoneyK(croNsr.daily.total)}/d` : undefined,
        valueRows: showMoney
          ? [
              {
                sign: '+',
                text: `Payable metal ${formatMoney(croNsr.daily.payableMetal)}/d`,
              },
              {
                sign: '-',
                text: `Treatment charge ${formatMoney(croNsr.daily.treatmentCharge)}/d`,
              },
              {
                sign: '-',
                text: `Impurity penalties ${formatMoney(croNsr.daily.penalties)}/d`,
              },
              {
                sign: '-',
                text: `Freight ${formatMoney(croNsr.daily.freight)}/d`,
              },
            ]
          : croRecoveryLeverRows(state),
        footnote: undefined,
      },
    ],
  };
}

const SHORTFALL_CASE_KPI: Record<string, string> = {
  'case-ag-grade': 'rd-kpi-ag-grade',
  'case-zn-recovery': 'rd-kpi-zn-rec',
  'case-p80': 'rd-kpi-mill-util',
};

function isForecastRiskCase(caseItem: Case): boolean {
  return (caseItem.affects?.leadHours ?? 0) > 0;
}

function caseIsBelowComparisonBasis(caseItem: Case, state: SessionState): boolean {
  if (isForecastRiskCase(caseItem)) return false;
  const readingId = SHORTFALL_CASE_KPI[caseItem.id];
  if (!readingId) return true;
  const basis = comparisonBasis(readingId, state);
  if (!basis) return true;
  const actual = kpiValueState(readingId, state);
  return deviationFromBasisPct(actual, basis) < -0.5;
}

export function performanceForRole(
  roleId: PerformanceRoleId,
  input: PerformanceInput,
  state: SessionState,
  options: PerformanceOptions = {},
): PerformanceViewModel {
  const { cases, loops, actions } = input;
  const showMoney = options.showMoney ?? true;
  const ACTUAL_TD_24H = kpiValueState('rd-kpi-ore-td', state);

  switch (roleId) {
    case 'gm': {
      const znBasis = comparisonBasis('rd-kpi-zn-rec', state);
      const pbBasis = comparisonBasis('rd-kpi-pb-rec', state);
      const tdBasis = oreTdBasis(state);
      const znStatus = znBasis
        ? deviationStatusPct(kpiValueState('rd-kpi-zn-rec', state), znBasis.value)
        : 'gap';
      const pbStatus = pbBasis
        ? deviationStatusPct(kpiValueState('rd-kpi-pb-rec', state), pbBasis.value)
        : 'gap';
      const throughputStatus = deviationStatusPct(ACTUAL_TD_24H, tdBasis.value);
      const tdCaption = basisCaption(ACTUAL_TD_24H, tdBasis);
      const znCaption = znBasis
        ? basisCaption(kpiValueState('rd-kpi-zn-rec', state), znBasis, 1, '%')
        : { primary: '—', secondary: undefined, basisValue: 0 };
      const pbCaption = pbBasis
        ? basisCaption(kpiValueState('rd-kpi-pb-rec', state), pbBasis, 1, '%')
        : { primary: '—', secondary: undefined, basisValue: 0 };
      const gmNsr = nsrRead(state);
      const gmCost = operatingCostRead(state);
      const designTd = kpiDesign('rd-kpi-ore-td');
      const rampPct = designTd > 0 ? Math.round((ACTUAL_TD_24H / designTd) * 100) : 80;
      const throughputCaption = tdCaption.secondary
        ? `${tdCaption.primary} · ${tdCaption.secondary} · Month ${PLANT.phaseMonth} · ${rampPct}% of design rate`
        : `${tdCaption.primary} · Month ${PLANT.phaseMonth} · ${rampPct}% of design rate`;

      return {
        // Names the basis that actually won, not a hardcoded word (pl.D1
        // round 2, #142 — matches the CRO/superintendent captions below).
        title: `Daily / weekly / MTD performance vs ${tdBasis.label.toLowerCase()} and design`,
        kpis: [
          {
            label: 'Throughput (24h)',
            value: `${formatNumber(ACTUAL_TD_24H)} t/d`,
            caption: throughputCaption,
            tone: deviationToTone(throughputStatus),
          },
          {
            label: 'Zn recovery',
            value: `${kpiCell('rd-kpi-zn-rec', 'value')}%`,
            caption: znCaption.secondary
              ? `${znCaption.primary} · ${znCaption.secondary}`
              : znCaption.primary,
            tone: deviationToTone(znStatus),
          },
          {
            label: 'Pb recovery',
            value: `${kpiCell('rd-kpi-pb-rec', 'value')}%`,
            caption: pbCaption.secondary
              ? `${pbCaption.primary} · ${pbCaption.secondary}`
              : pbCaption.primary,
            tone: deviationToTone(pbStatus),
          },
          ...(showMoney
            ? ([
                {
                  label: 'NSR revenue',
                  value: formatMoneyK(gmNsr.daily.total),
                  caption: `Today vs target ${formatMoneyK(gmNsr.budgetDaily)} · ${formatSignedPct(((gmNsr.daily.total - gmNsr.budgetDaily) / gmNsr.budgetDaily) * 100)}`,
                  provenance: NSR_SYN_PROVENANCE,
                  provenanceChip: '[syn]',
                  tone: (gmNsr.daily.total < gmNsr.budgetDaily * 0.98 ? 'warn' : 'ok') as VizTone,
                },
                {
                  label: 'Operating cost',
                  value: formatMoneyPerT(gmCost.actualPerT),
                  caption: `Target ${formatMoneyPerT(gmCost.targetPerT)} · ${formatSignedPct(gmCost.deltaPct)}`,
                  provenance: NSR_SYN_PROVENANCE,
                  provenanceChip: '[syn]',
                  tone: (gmCost.deltaPct > 2 ? 'bad' : 'ok') as VizTone,
                },
              ] satisfies PerformanceKpi[])
            : []),
        ],
        byAreaRows: buildByAreaRows(state),
        byAreaBasisLabel: tdBasis.label,
        planAchievement: buildPlanAchievement(3, state),
        functionHealth: functionHealthRead(state, cases),
        aiBand: buildFullAiBand(cases, state),
      };
    }

    case 'superintendent': {
      const dayMilled = kpiValueState('rd-kpi-milled-day', state);
      const nightMilled = kpiValueState('rd-kpi-milled-night', state);
      const shiftBasis = comparisonBasis('rd-kpi-milled-day', state);
      if (!shiftBasis) throw new Error('Missing shift comparison basis');
      const shiftTarget = shiftBasis.value;
      // The by-area table's own basis, not the shift KPI's — the shift
      // reading (milled-day) resolves to Design, but every by-area row
      // resolves to Budget (pl.D1 round 2, #142); read it, don't reuse
      // shiftBasis.label here.
      const byAreaBasis = comparisonBasis('rd-kpi-ore-td', state);
      const prodCases = cases.filter(
        (c) =>
          isOpenCase(c) &&
          (c.circuit.includes('Grinding') ||
            c.circuit.includes('Ball mill') ||
            c.circuit.includes('flotation') ||
            c.circuit.includes('Flotation')),
      );

      return {
        title: 'Shift / Weekly / MTD Performance',
        kpis: [
          {
            label: 'Day shift avg',
            value: `${formatNumber(dayMilled)} t`,
            caption: basisCaption(dayMilled, shiftBasis).primary,
            tone: deviationToTone(deviationStatusPct(dayMilled, shiftTarget)),
          },
          {
            label: 'Night shift avg',
            value: `${formatNumber(nightMilled)} t`,
            caption: basisCaption(nightMilled, shiftBasis).primary,
            tone: deviationToTone(deviationStatusPct(nightMilled, shiftTarget)),
          },
          {
            label: 'Day/Night gap',
            value: `${formatNumber(dayMilled - nightMilled)} t`,
            caption: 'Day minus night · this week',
            tone: 'warn',
          },
          {
            label: 'Open cases affecting production',
            value: String(prodCases.length),
            caption: 'Grinding / flotation circuits',
            tone: prodCases.length > 2 ? 'warn' : 'ok',
          },
        ],
        byAreaRows: buildByAreaRows(state),
        byAreaBasisLabel: byAreaBasis?.label ?? BASIS_LABEL.budget,
        planAchievement: buildPlanAchievement(2, state),
        aiBand: buildFullAiBand(
          cases,
          state,
          'AI briefing — today, going into the daily meeting',
          { shiftAttribution: true },
        ),
      };
    }

    case 'cro': {
      const feedReading = kpiValueState('rd-kpv-bm-feed', state);
      const feedSp = kpiDesign('rd-kpv-bm-feed');
      const manualCount = loopsManualPastHours(loops, 2).length;
      const grindingCases = cases.filter(
        (c) =>
          isOpenCase(c) &&
          (c.circuit.includes('Grinding') || c.circuit.includes('Ball mill')) &&
          c.id !== 'case-bm-brg',
      );
      const tdBasis = oreTdBasis(state);
      const shiftActual = kpiValueState('rd-kpi-milled-day', state);
      const shiftBasis = comparisonBasis('rd-kpi-milled-day', state);
      const croZnBasis = comparisonBasis('rd-kpi-zn-rec', state);
      // The shortfall attributed to named equipment cases is a physical attribution against
      // capacity, not a variance against the soft startup ramp — re-tie to the design point
      // (ruling #258 round 2, "the shortfall attribution").
      const shortfallBasis = tdBasis.design ?? tdBasis.value;
      const grindingEstimates = distributeShortfall(grindingCases, shortfallBasis - ACTUAL_TD_24H);

      return {
        // The shift KPI (below) is judged against whichever basis actually
        // won for the per-shift reading — name that basis, not a hardcoded
        // word (ruling #258 round 2, "captions vs tone"; fallback word
        // updated to 'budget' at pl.D1 round 2, #142 — 'ramp' no longer has
        // any live seed candidate for this reading to fall back to).
        title: `Shift performance vs ${(shiftBasis?.label ?? BASIS_LABEL.budget).toLowerCase()}`,
        kpis: [
          {
            label: 'Throughput — this shift',
            value: `${formatNumber(shiftActual)} t`,
            caption: shiftBasis ? basisCaption(shiftActual, shiftBasis).primary : undefined,
            tone: shiftBasis
              ? deviationToTone(deviationStatusPct(shiftActual, shiftBasis.value))
              : 'neutral',
          },
          {
            label: 'BM01 fresh feed',
            value: `${feedReading.toFixed(1)} stph`,
            caption: `Design ${feedSp.toFixed(1)} stph · WIT-021`,
            tone: feedReading < feedSp ? 'warn' : 'ok',
          },
          {
            label: 'Zn recovery — last assay',
            value: `${kpiCell('rd-kpi-zn-rec', 'value')}%`,
            caption: croZnBasis
              ? basisCaption(kpiValueState('rd-kpi-zn-rec', state), croZnBasis, 1, '%').primary
              : undefined,
            // The same comparison the Operator landing shows, through the
            // same rule (K10c item 5).
            tone: comparatorTone(
              kpiValueState('rd-kpi-zn-rec', state),
              kpiBasisValue('rd-kpi-zn-rec', state),
            ),
          },
          {
            label: 'Loops in Manual > 2h',
            value: String(manualCount),
            caption: manualCount > 0 ? 'Fine ore bin / cell level — constraining feed' : 'None',
            tone: manualCount > 0 ? 'warn' : 'ok',
          },
        ],
        grindingShortfall: grindingCases.map((caseItem, index) => ({
          caseId: caseItem.id,
          title: caseItem.title,
          tdEst: Math.abs(grindingEstimates[index] ?? 0),
        })),
        aiBand: buildCroAiBand(cases, actions, state, showMoney),
      };
    }

    case 'metallurgist': {
      const znKpi = metRecoveryKpiRead('Zn', state);
      const pbKpi = metRecoveryKpiRead('Pb', state);
      const collectorActual = kpiValue('rd-reag-aero');
      const collectorDesign = kpiDesign('rd-reag-aero');
      const collectorDelta =
        collectorDesign > 0
          ? `${collectorActual > collectorDesign ? '+' : ''}${Math.round(((collectorActual - collectorDesign) / collectorDesign) * 100)}%`
          : '—';

      return {
        title: 'Lab Trends & Reagent Performance',
        kpis: [
          {
            label: znKpi.label,
            value: znKpi.value,
            caption: znKpi.caption,
            provenance: znKpi.provenance,
            tone: znKpi.tone,
          },
          {
            label: 'Zn collector vs design dose',
            value: `${collectorActual} g/t`,
            caption: `Aero 5100 · ${collectorActual} vs ${collectorDesign} g/t · ${collectorDelta}`,
            tone: collectorActual > collectorDesign ? 'warn' : 'ok',
          },
          {
            label: pbKpi.label,
            value: pbKpi.value,
            caption: pbKpi.caption,
            provenance: pbKpi.provenance,
            tone: pbKpi.tone,
          },
          {
            label: 'Zn conc grade',
            value: `${kpiCell('rd-kpi-zn-grade', 'value')}%`,
            caption: (() => {
              const znGradeBasis = comparisonBasis('rd-kpi-zn-grade', state);
              const znActual = kpiValueState('rd-kpi-zn-grade', state);
              if (!znGradeBasis) return '—';
              const cap = basisCaption(znActual, znGradeBasis, 1, '%');
              return cap.secondary ? `${cap.primary} · ${cap.secondary}` : cap.primary;
            })(),
            tone: deviationToTone(
              deviationStatusPct(
                kpiValueState('rd-kpi-zn-grade', state),
                kpiBasisValue('rd-kpi-zn-grade', state),
              ),
            ),
          },
        ],
      };
    }

    case 'maintenance': {
      const maintCases = cases.filter((c) => isOpenCase(c) && c.assignedTo === 'maintenance');
      const tdBasis = oreTdBasis(state);
      // Physical attribution against capacity, not the soft startup ramp (ruling #258 round 2).
      const shortfallBasis = tdBasis.design ?? tdBasis.value;
      const gap = ACTUAL_TD_24H - shortfallBasis;

      return {
        // Names the tabs this landing actually renders — the design → actual
        // bridge the old header announced is not on the A2 tab landing, and
        // the other four glass landings carry a title alone.
        title: 'Equipment health, work orders and reliability trends',
        kpis: [
          {
            label: 'Throughput — today',
            value: `${formatNumber(ACTUAL_TD_24H)} t/d`,
            caption: basisCaption(ACTUAL_TD_24H, tdBasis).primary,
            tone: deviationToTone(deviationStatusPct(ACTUAL_TD_24H, tdBasis.value)),
          },
          {
            label: 'Gap to design',
            value: `${gap < 0 ? '-' : ''}${formatNumber(Math.abs(gap))} t/d`,
            caption: undefined,
            tone: 'bad',
          },
          {
            label: 'Open cases affecting throughput',
            value: String(maintCases.length),
            caption: 'Maintenance-assigned · open',
            tone: maintCases.length > 0 ? 'warn' : 'ok',
          },
          {
            label: 'Zn recovery',
            value: `${kpiCell('rd-kpi-zn-rec', 'value')}%`,
            caption: (() => {
              const znCap = basisCaption(
                kpiValueState('rd-kpi-zn-rec', state),
                comparisonBasis('rd-kpi-zn-rec', state)!,
                1,
                '%',
              );
              return znCap.secondary ? `${znCap.primary} · ${znCap.secondary}` : znCap.primary;
            })(),
            tone: basisCaptionTone(
              kpiValueState('rd-kpi-zn-rec', state),
              comparisonBasis('rd-kpi-zn-rec', state)!,
            ),
          },
        ],
      };
    }
  }
}

/** Default input from static seed — for Storybook / tests. */
export function defaultPerformanceInput(): PerformanceInput {
  return {
    cases: CASES,
    loops: CONTROL_LOOPS,
    actions: RECOMMENDED_ACTIONS,
  };
}
